import { useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { DragIcon, RefreshIcon, WarningIcon } from "@/assets/icons";
import EditPopup, {
  OFFSET_MAPPING
} from "@/components/dataset/datasetAttributeFilter/EditPopup";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion.tsx";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset.ts";
import { useDataset } from "@/hooks/map/useDataset.ts";
import { useAnalysisLegendConfig } from "@/hooks/spatialAnalysis/useAnalysisLegendConfig";
import { useGetAnalysisDatasets } from "@/hooks/spatialAnalysis/useGetAnalysisDatasets";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useStepValue } from "@/hooks/useStepValue";
import { cn } from "@/lib/utils.ts";
import {
  updateDatasetDisplayName,
  updateRawDatasetDisplayName
} from "@/store/slice/datasetSlice.ts";
import { renameDefaultWidgets } from "@/store/slice/widgetSlice";
import {
  ColorScaleMethod,
  Dataset,
  DatasetAttributeSchema,
  EColorLabel,
  EColorNames
} from "@/types/dataset.ts";
import { EOutputAnalysisType } from "@/types/spatial.ts";
import {
  getColorBasedOnDefault,
  getFillStepInputConfig
} from "@/utils/dataset.ts";
import { isRankableAttribute } from "@/utils/map/rank";
import { buildCustomLegendCallbacks } from "@/utils/mapLegend";

import { DraggableProvided } from "@hello-pangea/dnd";
import DOMPurify from "dompurify";
import { twMerge } from "tailwind-merge";

import ColorBasedSelect from "./ColorBasedSelect";
import ColorScaleSelect from "./ColorScaleSelect";
import { DatasetDisplayNameMenu } from "./DatasetDisplayNameMenu";
import DatasetItemMenu from "./DatasetItemMenu";
import { DatasetTypeIcon } from "./DatasetTypeIcon";
import OpacitySlider from "./OpacitySlider";

import { DatasetStyleSelection } from "./DatasetStyleSelection/DatasetStyleSelection";

interface DatasetItemProps {
  dragProvided?: DraggableProvided;
  data: Dataset;
  isFocused: boolean;
  isComparsionMode?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onRemoveDataset: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleDatasetVisibility: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onOpenDatasetTabularModal: () => void;
  onSelectAttribute?: (value: string) => void;
  onSelectStrokeAttribute?: (value: string) => void;
  onChangeColorStep?: (step: number) => void;
  onChangeStrokeColorStep?: (step: number) => void;
  isSpatialAnalysis?: boolean;
  titleColorBasedSelect?: string;
  isEditMode?: boolean; // When `true`, user can change color, opacity and ability to rename dataset
  /** Read-only surface (e.g. the Analysis Config view-only panel): the style
   * config stays visible and expandable, but nothing can be mutated — the
   * action menu is not rendered, rename is off, and the style controls are
   * disabled. Orthogonal to `isEditMode`, which hides those controls instead. */
  isReadOnly?: boolean;
  popupOffsetId?: keyof typeof OFFSET_MAPPING;
  onShowLabel?: (checked: boolean) => void;
  showLabel?: boolean;
  isNewUIDatasetConfigEnabled?: boolean;
  totalRows?: number;
  handleChangeRankChecked?: (value: boolean) => void;
  handleChangeMaxRankingSlider?: (value: number[]) => void;
  handleChangeMaxRankingInput?: (value: number) => void;
  handleRankConfigChangeOnSelectLabel?: (
    isEnabled: boolean,
    category: DatasetAttributeSchema
  ) => void;
  tourId?: string;
}

const DatasetItem = ({
  onMouseEnter,
  onMouseLeave,
  dragProvided,
  data,
  isFocused,
  isComparsionMode,
  onRemoveDataset,
  onToggleDatasetVisibility,
  onOpenDatasetTabularModal,
  isSpatialAnalysis = false,
  titleColorBasedSelect,
  isEditMode = true,
  isReadOnly = false,
  popupOffsetId = "default",
  onSelectAttribute,
  onSelectStrokeAttribute,
  onChangeColorStep,
  onChangeStrokeColorStep,
  onShowLabel,
  showLabel,
  totalRows = 0,
  handleChangeRankChecked,
  handleChangeMaxRankingSlider,
  handleChangeMaxRankingInput,
  handleRankConfigChangeOnSelectLabel,
  tourId
}: DatasetItemProps) => {
  const {
    isRenameDatasetEnabled,
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    isCustomMapVisualizationLegendEnabled
  } = useAppFeatures();
  const dispatch = useDispatch();
  const { stepValue, maxStepValue, setStepValue, resetStepValue } =
    useStepValue({
      totalRows
    });
  const datasetActions = useDataset();
  const {
    handleChangeColorDataset,
    handleDisplayName,
    handleDisplayNameVisibility,
    handleDisplayNameColor,
    handleChangeStrokeWidthDataset,
    handleChangeColorScaleMethod,
    handleChangeCustomCategoryStepCount,
    handleChangeCustomRangeStepCount,
    getAttributeNumericRange,
    getAttributeHasEmptyValues
  } = datasetActions;

  const { isError, refetch } = useGetAnalysisDatasets(data.data.id, {
    enabled: !isReadOnly
  });
  const { persistAnalysisLegendConfig, persistAnalysisLegendConfigDebounced } =
    useAnalysisLegendConfig();

  const [showEditPopup, setShowEditPopup] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const inputDisplayNameRef = useRef<HTMLInputElement>(null);
  // Will be use to reset the input field when editing is canceled
  const [tempDisplayName, setTempDisplayName] = useState<string>(
    data.data.display_name ?? data.data.name
  );

  // The value that will be used on input rename dataset
  const [localeDisplayName, setLocaleDisplayName] = useState<string>(
    data.data.display_name ?? data.data.name
  );

  const [showRenameDatasetInput, setShowRenameDatasetInput] = useState(false);

  const handleMouseLeave = () => {
    // Only trigger onMouseLeave if edit popup is not shown
    if (!showEditPopup) onMouseLeave();
  };

  const handleOpenDatasetTabularModal = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    onOpenDatasetTabularModal();
  };

  const isAvailable = data.data.available === true;
  const schema =
    data.data.schema.length > 0 ? data.data.schema : data.categories;

  // Pre-calculate strokeAttribute to avoid recalculating it in callbacks
  const strokeAttribute = data.categories.find(
    (cat) => cat.attribute_name === data.style.stroke?.attribute_name
  );

  const renderDatasetIcon = () => {
    // Forces the icon to be the grid icon for spatial analysis results dataset
    if (data.data.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME)
      return <DatasetTypeIcon type={EOutputAnalysisType.GRID} />;
    if (data.properties.isFetching) {
      return (
        <div
          className="bg-whisper p-0.5 rounded-[4px]"
          aria-label="Fetching icon"
        >
          <RefreshIcon className="fill-tangerine w-4 animate-spin" />
        </div>
      );
    }

    if (isError || !isAvailable) {
      return (
        <div className="bg-red-50 p-0.5 rounded-[4px] size-5">
          <WarningIcon
            className="fill-red-400 size-4"
            data-testid="warning-icon"
          />
        </div>
      );
    }

    return <DatasetTypeIcon type={data.properties.spatialType} />;
  };

  const handleDoubleClickDatasetname = () => {
    setShowRenameDatasetInput((showRenameState) => {
      if (showRenameState) {
        // when formerly show rename is true, trigger mouse enter
        // to directly show DatasetItemMenu
        onMouseEnter();
      } else {
        // when formerly show rename is false, trigger mouse leave
        // to directly hide DatasetItemMenu
        handleMouseLeave();
      }
      return !showRenameState;
    });
  };

  const handleCancelRename = () => {
    setShowRenameDatasetInput(false);
    setLocaleDisplayName(tempDisplayName);
  };

  const handleConfirmRename = () => {
    setShowRenameDatasetInput(false);
    setTempDisplayName(localeDisplayName);
    dispatch(
      updateDatasetDisplayName({
        datasetId: data.data.id,
        displayName: localeDisplayName
      })
    );
    dispatch(
      updateRawDatasetDisplayName({
        datasetId: data.data.id,
        displayName: localeDisplayName
      })
    );
    dispatch(
      renameDefaultWidgets({
        datasetId: data.data.id,
        label: localeDisplayName
      })
    );
  };

  const handleCheckDisplayLabel = (e: boolean) => {
    if (!isEditMode) {
      onShowLabel?.(e);
      return;
    }
    handleDisplayNameVisibility(data, e);
  };

  const handleSelectCategoryLabel = (selectedCategory: string) => {
    if (!isEditMode) {
      onSelectAttribute?.(selectedCategory);
      return;
    }
    const category = data.categories.find(
      (category) => category.attribute_name === selectedCategory
    );

    if (!category) return;

    const isEnabled = isRankableAttribute(schema, category.attribute_name);

    handleRankConfigChangeOnSelectLabel?.(isEnabled, category);
    handleDisplayName(data, category);
  };

  const handleChangeLabelColor = (color: string) => {
    handleDisplayNameColor(data, color as EColorLabel);
  };

  const handleStepValueChange = (step: number) => {
    setStepValue(step);
    if (step >= 2 && step <= 10) {
      onChangeColorStep?.(step);
    }
    persistAnalysisLegendConfigDebounced();
  };

  const handleStrokeStepValueChange = (step: number) => {
    setStepValue(step);
    if (step >= 2 && step <= 10) {
      onChangeStrokeColorStep?.(step);
    }
    persistAnalysisLegendConfigDebounced();
  };

  // The custom methods keep their own step count, and both panels share these.
  const handleCustomCategoryStepCount = (datasetId: string, step: number) => {
    handleChangeCustomCategoryStepCount(datasetId, step);
    persistAnalysisLegendConfigDebounced();
  };

  const handleCustomRangeStepCount = (datasetId: string, step: number) => {
    handleChangeCustomRangeStepCount(datasetId, step);
    persistAnalysisLegendConfigDebounced();
  };

  const { fillStepConfig, customRangeBounds, customRangeHasEmptyValues } =
    getFillStepInputConfig({
      data,
      isCustomMapVisualizationLegendEnabled,
      stepValue,
      setStepValue,
      getAttributeNumericRange,
      getAttributeHasEmptyValues,
      onChangeCustomCategoryStepCount: handleCustomCategoryStepCount,
      onChangeCustomRangeStepCount: handleCustomRangeStepCount,
      onDefaultStepChange: handleStepValueChange
    });

  // Stroke step input: same custom-method routing as fill, but a value-based
  // step change recolors the stroke (lines), not the fill.
  const { fillStepConfig: strokeStepConfig } = getFillStepInputConfig({
    data,
    isCustomMapVisualizationLegendEnabled,
    stepValue,
    setStepValue,
    getAttributeNumericRange,
    getAttributeHasEmptyValues,
    onChangeCustomCategoryStepCount: handleCustomCategoryStepCount,
    onChangeCustomRangeStepCount: handleCustomRangeStepCount,
    onDefaultStepChange: handleStrokeStepValueChange
  });

  // "Color Based On" has no Save button of its own, so the new active method is
  // persisted right away. The store update has already repainted the map, and a
  // failed save reports itself with a toast.
  const handleChangeColorBasedOn = async (value: string) => {
    handleChangeColorScaleMethod(data.data.id, value as ColorScaleMethod);
    await persistAnalysisLegendConfig();
  };

  // Shared by the Fill and (line) Stroke panels — same config slots, built once.
  // Each Save also persists on the Analysis Result panel; a no-op elsewhere.
  const {
    singleStyle: singleStyleCallbacks,
    customCategory: customCategoryCallbacks,
    customRange: customRangeCallbacks
  } = buildCustomLegendCallbacks(data.data.id, datasetActions, {
    resetStepValue,
    customRangeBounds,
    customRangeHasEmptyValues,
    onAfterSave: persistAnalysisLegendConfig
  });

  return (
    <li
      ref={dragProvided?.innerRef}
      {...dragProvided?.draggableProps}
      className={twMerge(
        "flex border-[1px] w-full font-sans border-gainsboro min-h-[32px] rounded-[12px] overflow-hidden",
        (!isAvailable || isError) && "border-cinnabar bg-white-smoke"
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-tourid={tourId}
    >
      <AccordionItem
        className={twMerge(
          "flex flex-col w-full pr-1 pl-5 relative justify-center",
          isComparsionMode && "h-full",
          data.properties.isFetching && "bg-white-smoke"
        )}
        data-testid="accordion-item"
        value={data.data.id}
      >
        <div className="flex items-center">
          {/* Left content */}
          <div className="h-full flex items-center gap-2 overflow-hidden flex-1">
            <div
              className="bg-whisper w-[14px] flex items-center justify-center h-full min-w-[14px] absolute left-0 top-0"
              {...dragProvided?.dragHandleProps}
            >
              <DragIcon />
            </div>
            <button
              type="button"
              className="flex items-center gap-1 overflow-hidden justify-start h-[30px] flex-1 truncate cursor-default"
              onDoubleClick={() => {
                // When on spatial analysis, the double click should not trigger rename
                if (
                  !isSpatialAnalysis &&
                  isRenameDatasetEnabled &&
                  isEditMode &&
                  !isReadOnly
                ) {
                  handleDoubleClickDatasetname();
                }
              }}
            >
              {renderDatasetIcon()}
              {isEditMode ? (
                <input
                  name={`dataset-display-name-${data.data.id}`}
                  ref={inputDisplayNameRef}
                  className={cn(
                    "text-[12px] text-txtHeading font-regular truncate outline-none bg-transparent w-full",
                    data.properties.isFetching && "text-txtBody",
                    !showRenameDatasetInput && "select-none"
                  )}
                  value={localeDisplayName}
                  readOnly={!showRenameDatasetInput}
                  maxLength={50}
                  onInput={(e) => {
                    setLocaleDisplayName(
                      DOMPurify.sanitize(e.currentTarget.value)
                    );
                  }}
                />
              ) : (
                <p
                  className={cn(
                    "flex-1 text-[12px] text-txtHeading font-regular truncate",
                    data.properties.isFetching && "text-txtBody"
                  )}
                >
                  {data.data.name}
                </p>
              )}
            </button>
          </div>
          {showRenameDatasetInput ? (
            <DatasetDisplayNameMenu
              isDisabled={!isAvailable || data.properties.isFetching || isError}
              onCancelRenameClicked={handleCancelRename}
              onConfirmRenameClicked={handleConfirmRename}
            />
          ) : (
            <>
              {/* Right content. Dropped entirely when read-only: every action it
                  hosts mutates state (visibility, remove, attribute filter) or
                  opens a surface scoped to the active analysis (data table). */}
              {!isReadOnly && (
                <DatasetItemMenu
                  isComparsionMode={isComparsionMode}
                  isSpatialAnalysis={isSpatialAnalysis}
                  handleOpenDatasetTabularModal={handleOpenDatasetTabularModal}
                  onRemoveDataset={onRemoveDataset}
                  showEditPopup={showEditPopup}
                  setShowEditPopup={setShowEditPopup}
                  editButtonRef={editButtonRef}
                  refetch={refetch}
                  isError={isError}
                  isFocused={isFocused}
                  onToggleDatasetVisibility={onToggleDatasetVisibility}
                  data={data}
                  isCatchment={popupOffsetId === "catchment"}
                />
              )}

              <AccordionTrigger
                className={twMerge([
                  "h-[30px]",
                  !isAvailable && [
                    "cursor-not-allowed",
                    "[&>svg]:stroke-light-gray"
                  ]
                ])}
                disabled={!isAvailable || data.properties.isFetching}
                data-testid="expand-dataset-trigger"
              />
            </>
          )}
        </div>
        {isAvailable && isNewUIDatasetConfigEnabled ? (
          <AccordionContent className="border-t border-gainsboro pt-2 flex flex-col gap-2">
            <Accordion
              type="single"
              collapsible
              className="flex flex-col gap-2 "
            >
              {isEditMode && (
                <DatasetStyleSelection
                  type="fill"
                  data={data}
                  isEditMode={isEditMode}
                  isLayerVisible={data.properties.isVisible}
                  isReadOnly={isReadOnly}
                  isSpatialAnalysis={isSpatialAnalysis}
                  isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
                  isCustomMapVisualizationLegendEnabled={
                    isCustomMapVisualizationLegendEnabled
                  }
                  categoryConfig={{
                    value: data.selectedCategory?.attribute_name,
                    onChange: onSelectAttribute,
                    title: titleColorBasedSelect
                  }}
                  colorBasedOnConfig={{
                    value: getColorBasedOnDefault(
                      data.style.colorScaleMethod,
                      data.selectedCategory?.data_type
                    ),
                    dataType: data.selectedCategory?.data_type,
                    onChange: handleChangeColorBasedOn
                  }}
                  colorConfig={{
                    value: data.style.colorFamily,
                    onChange: (selectedColorFamily) => {
                      handleChangeColorDataset(
                        data.data.id,
                        selectedColorFamily as EColorNames,
                        data.style.colorRanges,
                        data.selectedCategory.attribute_name,
                        data.properties.spatialType,
                        data.selectedCategory.data_type,
                        stepValue ?? data.style.colorSteps
                      );
                      persistAnalysisLegendConfigDebounced();
                    },
                    ...fillStepConfig
                  }}
                  singleStyle={singleStyleCallbacks}
                  customCategory={customCategoryCallbacks}
                  customRange={customRangeCallbacks}
                  totalRows={totalRows}
                />
              )}
              <DatasetStyleSelection
                type="label"
                data={data}
                totalRows={totalRows}
                isEditMode={isEditMode}
                isLayerVisible={data.properties.isVisible}
                isReadOnly={isReadOnly}
                isSpatialAnalysis={isSpatialAnalysis}
                isRankingLabelEnabled={isRankingLabelEnabled}
                categoryConfig={{
                  value: data.style.labelConfig?.attribute_name || "",
                  onChange: handleSelectCategoryLabel,
                  title: "Column Option"
                }}
                colorConfig={{
                  value: data.style.labelConfig?.color_scale,
                  onChange: handleChangeLabelColor
                }}
                labelConfig={{
                  show: showLabel,
                  onCheck: handleCheckDisplayLabel
                }}
                rankConfig={{
                  onCheck: handleChangeRankChecked,
                  onChangeInput: handleChangeMaxRankingInput,
                  onChangeSlider: handleChangeMaxRankingSlider
                }}
              />

              <DatasetStyleSelection
                type="stroke"
                isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
                data={data}
                totalRows={totalRows}
                isEditMode={isEditMode}
                isLayerVisible={data.properties.isVisible}
                isReadOnly={isReadOnly}
                isSpatialAnalysis={isSpatialAnalysis}
                isRankingLabelEnabled={isRankingLabelEnabled}
                isCustomMapVisualizationLegendEnabled={
                  isCustomMapVisualizationLegendEnabled
                }
                categoryConfig={{
                  value: data.style.stroke?.attribute_name || "",
                  onChange: onSelectStrokeAttribute,
                  title: "Column Option"
                }}
                // Lines style the stroke via the custom legend (method + config
                // shared with fill); applicable methods come from the stroke type.
                colorBasedOnConfig={{
                  value: getColorBasedOnDefault(
                    data.style.colorScaleMethod,
                    strokeAttribute?.data_type ||
                      data.selectedCategory.data_type
                  ),
                  dataType:
                    strokeAttribute?.data_type ||
                    data.selectedCategory.data_type,
                  onChange: handleChangeColorBasedOn
                }}
                colorConfig={{
                  value: data.style.colorFamily,
                  onChange: (selectedColorFamily) => {
                    handleChangeColorDataset(
                      data.data.id,
                      selectedColorFamily as EColorNames,
                      data.style.colorRanges,
                      data.style.stroke?.attribute_name ||
                        data.selectedCategory.attribute_name,
                      data.properties.spatialType,
                      strokeAttribute?.data_type ||
                        data.selectedCategory.data_type,
                      stepValue
                    );
                    persistAnalysisLegendConfigDebounced();
                  },
                  ...strokeStepConfig
                }}
                singleStyle={singleStyleCallbacks}
                customCategory={customCategoryCallbacks}
                customRange={customRangeCallbacks}
                strokeConfig={{
                  value: data.style.stroke.width,
                  onChange: (width: number) => {
                    handleChangeStrokeWidthDataset(data.data.id, width);
                  }
                }}
              />
            </Accordion>
          </AccordionContent>
        ) : (
          <AccordionContent className="flex flex-col gap-3">
            <ColorBasedSelect
              value={data.selectedCategory?.attribute_name}
              onValueChange={(value) => onSelectAttribute?.(value)}
              categories={data.categories}
              title={titleColorBasedSelect}
              disabled={!data.properties.isVisible || isReadOnly}
            />
            {isEditMode && (
              <ColorScaleSelect
                value={data.style.colorFamily}
                onValueChange={(selectedColorFamily) => {
                  handleChangeColorDataset(
                    data.data.id,
                    selectedColorFamily as EColorNames,
                    data.style.colorRanges,
                    data.selectedCategory.attribute_name,
                    data.properties.spatialType,
                    data.selectedCategory.data_type
                  );
                }}
                isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
                stepValue={stepValue}
                maxStepValue={maxStepValue}
                onStepValueChange={setStepValue}
                disabled={isReadOnly}
              />
            )}
            {isEditMode && (
              <OpacitySlider
                data={data}
                isSpatialAnalysis={isSpatialAnalysis}
                disabled={isReadOnly}
              />
            )}
          </AccordionContent>
        )}
        {isAvailable && showEditPopup && (
          <EditPopup
            dataset={data}
            offsetId={popupOffsetId}
            onClose={() => setShowEditPopup(false)}
          />
        )}
      </AccordionItem>
    </li>
  );
};

export default DatasetItem;
